import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SchoolIcon from '@mui/icons-material/School';
import PrintIcon from '@mui/icons-material/Print';
import CompareIcon from '@mui/icons-material/Compare';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PdfDocument from './PdfDocument';
import { resultsService } from '../api/resultsService';

// Helper function to ensure solution text is always a string
const getSolutionText = (solution) => {
  if (!solution) return "";
  if (typeof solution === 'string') return solution;
  if (typeof solution === 'object') {
    // If solution is an object with a text property, use that
    if (solution.text) return solution.text;
    // Otherwise, convert the object to a string representation
    return JSON.stringify(solution);
  }
  // Default case, convert to string
  return String(solution);
};

const QuestionsList = ({ 
  questions, 
  onSolveQuestion, 
  onSolveAll,
  onSolveAllWithOpenAI,
  onSolveAllWithAnthropic,
  onSolveWithOpenAI,
  onSolveWithAnthropic,
  onSolveQuestionWithImage,
  onSolveWithOpenAIVision,
  processingQuestions = [],
  processingOpenAIQuestions = [],
  processingAnthropicQuestions = [] 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState({
    subject: 'All',
    status: 'All'
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleFilterChange = (event) => {
    setFilter({
      ...filter,
      [event.target.name]: event.target.value
    });
  };

  const filteredQuestions = questions.filter(question => {
    if (filter.subject !== 'All' && question.subject !== filter.subject) {
      return false;
    }
    if (filter.status === 'Solved' && !question.solution) {
      return false;
    }
    if (filter.status === 'Unsolved' && question.solution) {
      return false;
    }
    return true;
  });

  // Count questions by subject
  const subjectCounts = questions.reduce((acc, question) => {
    acc[question.subject] = (acc[question.subject] || 0) + 1;
    return acc;
  }, {});

  // Count solved/unsolved
  const geminiSolvedCount = questions.filter(q => q.geminiAnswer).length;
  const openaiSolvedCount = questions.filter(q => q.openaiAnswer).length;
  const anthropicSolvedCount = questions.filter(q => q.anthropicAnswer).length;
  const totalCount = questions.length;
  const geminiUnsolvedCount = totalCount - geminiSolvedCount;
  const openaiUnsolvedCount = totalCount - openaiSolvedCount;
  const anthropicUnsolvedCount = totalCount - anthropicSolvedCount;
  const isProcessingSome = processingQuestions.length > 0;
  const isProcessingOpenAISome = processingOpenAIQuestions.length > 0;
  const isProcessingAnthropicSome = processingAnthropicQuestions.length > 0;
  const allQuestionsProcessing = processingQuestions.length === geminiUnsolvedCount && geminiUnsolvedCount > 0;

  // Function to add a question to the comparison table
  const handleAddToComparison = (question) => {
    try {
      // Create a new result entry with both answers and solutions
      const resultData = {
        id: question.id,
        questionNumber: question.questionNumber,
        subject: question.subject,
        question: question.text,
        geminiAnswer: question.geminiAnswer || null,
        geminiSolution: question.geminiSolution || null,
        openaiAnswer: question.openaiAnswer || null,
        openaiSolution: question.openaiSolution || null,
        anthropicAnswer: question.anthropicAnswer || null,
        anthropicSolution: question.anthropicSolution || null,
        actualCorrectAnswer: null,
        options: question.options
      };
      
      resultsService.saveResult(resultData);
      setNotification({
        open: true,
        message: `Question ${question.questionNumber} added to comparison table`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding question to comparison:', error);
      setNotification({
        open: true,
        message: `Error adding question: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Function to add all visible questions to the comparison table
  const handleAddAllToComparison = () => {
    try {
      let addedCount = 0;
      filteredQuestions.forEach(question => {
        // Create result data for each question
        const resultData = {
          id: question.id,
          questionNumber: question.questionNumber,
          subject: question.subject,
          question: question.text,
          geminiAnswer: question.geminiAnswer || null,
          geminiSolution: question.geminiSolution || null,
          openaiAnswer: question.openaiAnswer || null,
          openaiSolution: question.openaiSolution || null,
          anthropicAnswer: question.anthropicAnswer || null,
          anthropicSolution: question.anthropicSolution || null,
          actualCorrectAnswer: null,
          options: question.options
        };
        
        if (resultsService.saveResult(resultData)) {
          addedCount++;
        }
      });
      
      setNotification({
        open: true,
        message: `Added ${addedCount} questions to comparison table`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding questions to comparison:', error);
      setNotification({
        open: true,
        message: `Error adding questions: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Extracted Questions ({questions.length})
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSolveAll}
            disabled={allQuestionsProcessing || geminiUnsolvedCount === 0}
            startIcon={allQuestionsProcessing ? <CircularProgress size={24} color="inherit" /> : <LightbulbIcon />}
          >
            {allQuestionsProcessing ? 'Processing All...' : `Solve All with Gemini (${geminiUnsolvedCount})`}
          </Button>

          <Button
            variant="contained"
            color="info"
            onClick={onSolveAllWithOpenAI}
            disabled={isProcessingOpenAISome || openaiUnsolvedCount === 0}
            startIcon={isProcessingOpenAISome ? <CircularProgress size={24} color="inherit" /> : <SmartToyIcon />}
          >
            {isProcessingOpenAISome ? 'Processing OpenAI...' : `Solve All with OpenAI (${openaiUnsolvedCount})`}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={onSolveAllWithAnthropic}
            disabled={isProcessingAnthropicSome || anthropicUnsolvedCount === 0}
            startIcon={isProcessingAnthropicSome ? <CircularProgress size={24} color="inherit" /> : <SchoolIcon />}
          >
            {isProcessingAnthropicSome ? 'Processing Anthropic...' : `Solve All with Anthropic (${anthropicUnsolvedCount})`}
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleAddAllToComparison}
            startIcon={<CompareIcon />}
            disabled={filteredQuestions.length === 0}
          >
            Add All to Comparison
          </Button>
          
          <PDFDownloadLink
            document={<PdfDocument questions={questions} />}
            fileName="neet_questions_solutions.pdf"
            style={{ textDecoration: 'none' }}
          >
            {({ loading: pdfLoading }) => (
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                disabled={pdfLoading}
              >
                {pdfLoading ? 'Generating PDF...' : 'Export PDF'}
              </Button>
            )}
          </PDFDownloadLink>
        </Box>
      </Box>

      {(isProcessingSome || isProcessingOpenAISome || isProcessingAnthropicSome) && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="body1" color="info.main">
            <CircularProgress size={16} sx={{ mr: 1, verticalAlign: 'middle' }} />
            Generating solutions... {processingQuestions.length + processingOpenAIQuestions.length + processingAnthropicQuestions.length} question{(processingQuestions.length + processingOpenAIQuestions.length + processingAnthropicQuestions.length) > 1 ? 's' : ''} being processed.
            Solutions will appear automatically as they are generated.
          </Typography>
        </Box>
      )}
      
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle1" gutterBottom>
          <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Filter Questions
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Subject</InputLabel>
              <Select
                name="subject"
                value={filter.subject}
                label="Subject"
                onChange={handleFilterChange}
              >
                <MenuItem value="All">All Subjects</MenuItem>
                {Object.keys(subjectCounts).map(subject => (
                  <MenuItem key={subject} value={subject}>
                    {subject} ({subjectCounts[subject]})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filter.status}
                label="Status"
                onChange={handleFilterChange}
              >
                <MenuItem value="All">All Status</MenuItem>
                <MenuItem value="Solved">Solved ({Math.max(geminiSolvedCount, openaiSolvedCount, anthropicSolvedCount)})</MenuItem>
                <MenuItem value="Unsolved">Unsolved ({Math.min(geminiUnsolvedCount, openaiUnsolvedCount, anthropicUnsolvedCount)})</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      
      {filteredQuestions.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
          No questions match your current filters.
        </Typography>
      ) : (
        filteredQuestions.map((question) => {
          const isProcessing = processingQuestions.includes(question.id);
          const isProcessingOpenAI = processingOpenAIQuestions.includes(question.id);
          const isProcessingAnthropic = processingAnthropicQuestions.includes(question.id);
          return (
            <Accordion
              key={question.id}
              expanded={expanded === question.id || isProcessing || isProcessingOpenAI || isProcessingAnthropic}
              onChange={handleChange(question.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: '#f9f9f9' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography sx={{ width: '10%', flexShrink: 0, fontWeight: 'bold' }}>
                    Q{question.questionNumber}
                  </Typography>
                  <Typography sx={{ flexGrow: 1 }}>
                    {question.text.length > 100 
                      ? `${question.text.substring(0, 100)}...` 
                      : question.text}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      size="small" 
                      label={question.subject} 
                      color={
                        question.subject === 'Biology' ? 'success' :
                        question.subject === 'Chemistry' ? 'secondary' : 'primary'
                      }
                    />
                    <Chip 
                      size="small" 
                      label={isProcessing || isProcessingOpenAI || isProcessingAnthropic ? 'Processing' : question.solution ? 'Solved' : 'Unsolved'} 
                      color={
                        isProcessing || isProcessingOpenAI || isProcessingAnthropic ? 'info' :
                        question.solution ? 'success' : 'default'
                      }
                      variant="outlined"
                      icon={(isProcessing || isProcessingOpenAI || isProcessingAnthropic) ? <CircularProgress size={14} color="inherit" /> : undefined}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Question:
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {question.text}
                  </Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Options:
                  </Typography>
                  <Box sx={{ ml: 2 }}>
                    {Object.entries(question.options).map(([key, value]) => (
                      <Typography 
                        key={key} 
                        variant="body1" 
                        paragraph
                        sx={{ 
                          fontWeight: question.solution && key === question.correctAnswer ? 'bold' : 'normal',
                          color: question.solution && key === question.correctAnswer ? 'success.main' : 'inherit'
                        }}
                      >
                        {key}. {value}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {(question.geminiSolution || question.openaiSolution || question.anthropicSolution || isProcessing || isProcessingOpenAI || isProcessingAnthropic) ? (
                  <Box>
                    {/* Gemini Solution */}
                    {question.geminiSolution && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <LightbulbIcon sx={{ mr: 1 }} />
                          Gemini Solution:
                          {question.geminiAnswer && (
                            <Chip 
                              size="small" 
                              label={`Answer: ${question.geminiAnswer}`}
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f5f8ff' }}>
                          <Typography variant="body1" component="div">
                            {getSolutionText(question.geminiSolution).split('\n').map((line, i) => (
                              <Typography key={i} variant="body1" paragraph={i !== getSolutionText(question.geminiSolution).split('\n').length - 1}>
                                {line}
                              </Typography>
                            ))}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {/* OpenAI Solution */}
                    {question.openaiSolution && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <SmartToyIcon sx={{ mr: 1 }} />
                          OpenAI Solution:
                          {question.openaiAnswer && (
                            <Chip 
                              size="small" 
                              label={`Answer: ${question.openaiAnswer}`}
                              color="info"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f0f7ff' }}>
                          <Typography variant="body1" component="div">
                            {getSolutionText(question.openaiSolution).split('\n').map((line, i) => (
                              <Typography key={i} variant="body1" paragraph={i !== getSolutionText(question.openaiSolution).split('\n').length - 1}>
                                {line}
                              </Typography>
                            ))}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {/* Anthropic Solution */}
                    {question.anthropicSolution && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                          <SchoolIcon sx={{ mr: 1 }} />
                          Anthropic Solution:
                          {question.anthropicAnswer && (
                            <Chip 
                              size="small" 
                              label={`Answer: ${question.anthropicAnswer}`}
                              color="secondary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: '#fdf5ff' }}>
                          <Typography variant="body1" component="div">
                            {getSolutionText(question.anthropicSolution).split('\n').map((line, i) => (
                              <Typography key={i} variant="body1" paragraph={i !== getSolutionText(question.anthropicSolution).split('\n').length - 1}>
                                {line}
                              </Typography>
                            ))}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {/* Processing indicators */}
                    {(isProcessing || isProcessingOpenAI || isProcessingAnthropic) && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column' }}>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          Generating {isProcessing ? 'Gemini' : isProcessingOpenAI ? 'OpenAI' : 'Anthropic'} solution for this question...
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Add to comparison button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleAddToComparison(question)}
                        startIcon={<CompareIcon />}
                        size="small"
                      >
                        Add to AI Model Comparison
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">Text-based:</Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => onSolveQuestion(question.id)}
                        disabled={isProcessing || isProcessingOpenAI || isProcessingAnthropic}
                        startIcon={<LightbulbIcon />}
                      >
                        Solve Using Gemini
                      </Button>
                      
                      <Button
                        variant="contained"
                        color="info"
                        onClick={() => onSolveWithOpenAI(question.id)}
                        disabled={isProcessing || isProcessingOpenAI || isProcessingAnthropic}
                        startIcon={<SmartToyIcon />}
                      >
                        Solve Using OpenAI
                      </Button>

                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => onSolveWithAnthropic(question.id)}
                        disabled={isProcessing || isProcessingOpenAI || isProcessingAnthropic}
                        startIcon={<SchoolIcon />}
                      >
                        Solve Using Anthropic
                      </Button>
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="subtitle2" color="textSecondary">Image-based:</Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => onSolveQuestionWithImage(question.id)}
                        disabled={isProcessing || isProcessingOpenAI || isProcessingAnthropic || !question.imageSource}
                        startIcon={<LightbulbIcon />}
                      >
                        Vision Solve (Gemini)
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={() => onSolveWithOpenAIVision(question.id)}
                        disabled={isProcessing || isProcessingOpenAI || isProcessingAnthropic || !question.imageSource}
                        startIcon={<SmartToyIcon />}
                      >
                        Vision Solve (OpenAI)
                      </Button>
                    </Box>
                  </Box>
                )}
                
                {question.solution && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleAddToComparison(question)}
                      startIcon={<CompareIcon />}
                      size="small"
                    >
                      Add to AI Model Comparison
                    </Button>
                  </Box>
                )}
                
                {question.imageSource && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Source Image:
                    </Typography>
                    <Box
                      component="img"
                      src={question.imageSource}
                      alt={`Question ${question.questionNumber} source`}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        objectFit: 'contain',
                        border: '1px solid #eee',
                        borderRadius: 1
                      }}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default QuestionsList;